import { useQuery } from '@apollo/client';
import { graphql, useStaticQuery } from 'gatsby';
import React, { FC, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useDeepCompareEffect } from 'react-use';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import FlexColumn from '@ui/atoms/FlexColumn';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import Skeleton, { ISkeletonProps } from '@ui/atoms/Skeleton';
import Toast from '@ui/atoms/Toast';

import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import useDeliveryMethods from '@hooks/useDeliveryMethods';
import useMemberDefaultPharmacy from '@hooks/useMemberDefaultPharmacy';
import useMemberInfo, { IFragment, SubscriptionInfo } from '@hooks/useMemberInfo';
import useMemberInsurance from '@hooks/useMemberInsurance';
import { useMemberValidation } from '@hooks/useMemberValidation';
import usePaymentMethod from '@hooks/usePaymentMethod';

import { isCapsuleEligible } from '@helpers/Delivery';

import { keysToCamel } from '@utils/butter';
import { doesNotExist } from '@utils/general';

import { useAuth } from '@providers/AuthProvider';
import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import { IAddressInfoQueryData } from '@folx-types/address';
import { IDefaultMedicationDeliveryMethodResponse } from '@folx-types/member';

import CheckCircle from '@assets/vectors/check-circle-solid.inline.svg';
import ExclamationCircle from '@assets/vectors/exclamation-circle-solid.inline.svg';

import { membership } from '@graphql/fragments/membership';
import ADDRESS_INFO_QUERY from '@graphql/queries/AddressInfo.query.gql';
import BillingPageQuery from '@graphql/queries/BillingPage.query.gql';
import defaultMedicationDeliveryMethod from '@graphql/queries/defaultMedicationDeliveryMethod.query.gql';

import theme from '@theme/FolxTheme';

import Billing, { IBillingPageContent, IBillingPageInformation } from './Billing.component';

interface IBillingProps {
	path?: string;
}

const FullWidthColumn = fullWidth(FlexColumn);
const SpacedSkeleton = spaced<ISkeletonProps>(Skeleton);

const CardSkeleton = styled(Skeleton)`
	height: 20rem;
`;

const BillingPage: FC<IBillingProps> = (props) => {
	const { loggedIn } = useAuth();
	const [capsuleEligible, setCapsuleEligible] = useState(false);
	const { client: graphQLv2Client } = useContext(GraphQLv2Context);

	const { data: billingPageInformation, loading: loadingBillingPageInformation } =
		useQuery<IBillingPageInformation>(BillingPageQuery, {
			client: graphQLv2Client,
		});

	const {
		data: defaultPharmacyData,
		loading: defaultPharmacyLoading,
		error: defaultPharmacyError,
	} = useMemberDefaultPharmacy();

	const {
		memberInsurance,
		loading: insuranceInfoLoading,
		error: insuranceInfoError,
	} = useMemberInsurance();

	const fragments: IFragment[] = [{ name: 'Membership', node: membership }];
	const { loading: memberInfoLoading, memberInfo } = useMemberInfo<SubscriptionInfo>(fragments);

	const {
		loading: addressLoading,
		data: addressData,
		error: addressError,
	} = useQuery<IAddressInfoQueryData, { memberId: string | undefined }>(ADDRESS_INFO_QUERY, {
		skip: !memberInfo,
		variables: {
			memberId: memberInfo?.id,
		},
	});

	const { loading: defaultDeliveryMethodLoading, error: defaultDeliveryMethodError } =
		useQuery<IDefaultMedicationDeliveryMethodResponse>(defaultMedicationDeliveryMethod, {
			skip: !loggedIn,
		});

	const {
		value: deliveryData,
		error: deliveryError,
		loading: deliveryLoading,
	} = useDeliveryMethods();

	const { pageContent } = useStaticQuery(
		graphql`
			{
				pageContent: butterPage(slug: { eq: "mssp-billing-page" }) {
					winback_toast
					cancel_toast
					preferred_pharmacy_text
					bills_section_heading
					bills_label
					folx_membership {
						title_monthly
						title_yearly
						eyebrow_active_yearly
						eyebrow_active
						badge_active
						cta_active
						eyebrow_past_due
						badge_past_due
						description_past_due
						text_past_due
						cta_past_due
						eyebrow_canceled
						badge_canceled
						description_canceled
						cta_canceled
						eyebrow_b2b
						description_b2b
						text_b2b
						secondary_cta_past_due
						secondary_cta_switch_to_yearly
						secondary_cta_switch_to_monthly
					}
				}
			}
		`
	);

	const address = addressData?.addressByMemberIdAndType;

	const {
		value: paymentMethod,
		loading: paymentMethodLoading,
		error: paymentMethodError,
	} = usePaymentMethod();

	useDeepCompareEffect(() => {
		if (!paymentMethodError) return;
		console.log(paymentMethodError);
	}, [paymentMethodError]);

	useDeepCompareEffect(() => {
		if (!defaultPharmacyError) return;
		console.log(defaultPharmacyError);
	}, [defaultPharmacyError]);

	useDeepCompareEffect(() => {
		if (!insuranceInfoError) return;
		console.log(insuranceInfoError);
	}, [insuranceInfoError]);

	useDeepCompareEffect(() => {
		if (!deliveryError) return;
		console.log(deliveryError);
	}, [deliveryError]);

	useDeepCompareEffect(() => {
		if (!defaultDeliveryMethodError) return;
		console.log(defaultDeliveryMethodError);
	}, [defaultDeliveryMethodError]);

	useDeepCompareEffect(() => {
		if (!addressError) return;
		console.log(addressError);
	}, [addressError]);

	useDeepCompareEffect(() => {
		if (doesNotExist(deliveryData)) {
			return;
		}
		setCapsuleEligible(isCapsuleEligible(deliveryData));
	}, [deliveryData]);

	const { loaded: memberValidationLoaded, protectedNavigate } = useMemberValidation();

	const loading =
		memberInfoLoading ||
		addressLoading ||
		deliveryLoading ||
		paymentMethodLoading ||
		defaultDeliveryMethodLoading ||
		defaultPharmacyLoading ||
		insuranceInfoLoading ||
		loadingBillingPageInformation ||
		!memberValidationLoaded;

	const skeletonSection = (
		<FlexColumn gap="sm">
			<Skeleton height="md" fullWidth />
			<CardSkeleton fullWidth structure="single" />
		</FlexColumn>
	);

	const skeleton = (
		<FullWidthColumn gap="lg">
			<SpacedSkeleton height="lg" fullWidth marginBottom="xs" />
			{skeletonSection}
			{skeletonSection}
			{skeletonSection}
			{skeletonSection}
		</FullWidthColumn>
	);

	const refinedContent: IBillingPageContent = keysToCamel(pageContent) as IBillingPageContent;

	useDeepCompareEffect(() => {
		if (props?.location?.state?.winback) {
			toast.info(<BasicText color="white">{refinedContent.winbackToast}</BasicText>, {
				icon: <CheckCircle height={theme.spacing.lg} width={theme.spacing.lg} />,
			});
		}
		if (props?.location?.state?.cancel) {
			toast.info(<BasicText color="white">{refinedContent.cancelToast}</BasicText>, {
				icon: <ExclamationCircle height={theme.spacing.lg} width={theme.spacing.lg} />,
			});
		}
	}, [props?.location?.state]);

	return (
		<>
			<Toast
				position="top-center"
				theme="colored"
				pauseOnHover
				pauseOnFocusLoss={false}
				autoClose={7500}
			/>
			<LoadingWrapper loading={loading} skeleton={skeleton}>
				<Billing
					member={memberInfo}
					billingPageInformation={billingPageInformation}
					address={address}
					currentPaymentInfo={paymentMethod}
					deliveryMethod={defaultPharmacyData?.defaultMedicationDeliveryMethod}
					defaultPharmacy={defaultPharmacyData}
					memberInsurance={memberInsurance}
					capsuleEligible={capsuleEligible}
					hasActiveSub={memberInfo?.membershipSubscription?.status === 'ACTIVE'}
					protectedNavigate={protectedNavigate}
					pageContent={refinedContent}
				/>
			</LoadingWrapper>
		</>
	);
};

export default BillingPage;
